import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nav } from "@/content/site";
import { Logo } from "@/components/common/Logo";
import { ButtonLink } from "@/components/common/ButtonLink";
import { ThemeToggle } from "./ThemeToggle";
import { getLenis } from "./SmoothScroll";

const NavItem = ({ item, className, onClick, testId, children }) =>
  item.to.includes("#") ? (
    <Link to={item.to} onClick={onClick} data-testid={testId} className={typeof className === "function" ? className({ isActive: false }) : className}>
      {typeof children === "function" ? children({ isActive: false }) : children}
    </Link>
  ) : (
    <NavLink to={item.to} onClick={onClick} data-testid={testId} className={className}>
      {children}
    </NavLink>
  );

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-paper px-6 pb-10 pt-24 xl:hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    data-testid="mobile-menu"
  >
    <nav className="flex flex-col gap-1" aria-label="Mobile">
      {nav.map((item, i) => (
        <motion.div key={item.to} initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.05 + i * 0.06 }}>
          <NavItem
            item={item}
            onClick={onClose}
            testId={`mobile-nav-${item.id}`}
            className={({ isActive }) => `flex items-baseline gap-4 border-b border-line py-3.5 font-display text-[1.75rem] font-semibold tracking-[-0.03em] ${isActive ? "text-accent" : "text-ink"}`}
          >
            <span className="font-mono text-[12px] font-normal tracking-normal text-muted">{String(i + 1).padStart(2, "0")}</span>
            {item.label}
          </NavItem>
        </motion.div>
      ))}
    </nav>
    <div className="mt-auto pt-8">
      <ButtonLink to="/contact" variant="accent" onClick={onClose} testId="mobile-menu-cta">
        Start a conversation
      </ButtonLink>
    </div>
  </motion.div>
);

export const Header = ({ theme, onToggleTheme }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const lenis = getLenis();
    if (open) lenis?.stop();
    else lenis?.start();
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || open ? "border-b border-line bg-paper/90 backdrop-blur-xl" : "border-b border-line/60 bg-paper/60 backdrop-blur-md"
        }`}
        data-testid="site-header"
      >
        <div className="container-x flex h-[72px] items-center justify-between gap-6">
          <Link to="/" aria-label="SPC home" data-testid="header-logo-link" className="text-ink">
            <Logo className="h-9" />
          </Link>
          <nav className="hidden items-center gap-7 xl:flex" aria-label="Primary">
            {nav.map((item) => (
              <NavItem key={item.to} item={item} testId={`nav-${item.id}`} className="relative py-2 text-[14px] font-medium text-ink">
                {({ isActive }) => (
                  <>
                    <span className={isActive ? "text-accent" : "transition-colors hover:text-accent"}>{item.label}</span>
                    {isActive && <motion.span layoutId="nav-underline" className="absolute inset-x-0 -bottom-[17px] h-[2px] bg-accent" />}
                  </>
                )}
              </NavItem>
            ))}
          </nav>
          <div className="flex items-center gap-2.5">
            <ThemeToggle theme={theme} onToggle={onToggleTheme} />
            <ButtonLink to="/contact" className="hidden !py-2.5 xl:inline-flex" testId="header-cta-button">
              Start a conversation
            </ButtonLink>
            <button
              type="button"
              className="grid h-10 w-10 place-items-center rounded-none border border-line xl:hidden"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
};
